import {
  TaskCode,
  GroupSecurity,
  createReference,
  getReferenceData,
} from '@quromedical/fhir-common';
import { useNavigation } from '@react-navigation/native';
import { WithGroup } from 'core/auth';
import { PatientHeaderSection, CustomAction } from 'core/patient';
import { Assignment, AssignmentResult, NavigateToTask, TaskInbox } from 'core/task';
import { Section } from 'design-system';
import { usePatientTasks } from 'hooks/useTasks';
import { TaskApi } from 'integration/aggregate';
import { PatientDrawerScreenProps } from 'navigation';
import { usePatientId } from 'providers/PatientIdContext';
import React, { useCallback } from 'react';

const taskApi = new TaskApi();

const taskCodes: TaskCode[] = [
  TaskCode.OtherContact,
  TaskCode.OtherSchedule,
  TaskCode.ServiceRequestCreate,
  TaskCode.MedicationRequestCreate,
  TaskCode.OtherHomeVisit,
  TaskCode.OtherClinicalCallOut,
];

const PatientHeaderAssignmentButton: React.FC<CustomAction> = ({ id, patient, create }) => {
  const onSubmit = useCallback(
    async (result?: AssignmentResult) => {
      if (!result) {
        return;
      }

      const referenceData = getReferenceData({ reference: result.performer });

      if (!referenceData) {
        return;
      }

      await create({
        patientId: id,
        code: result.code,
        description: result.note,
        performer: createReference(referenceData.resourceType, referenceData.id),
        requestedPeriodEnd: result.dueDate ? result.dueDate.toString() : undefined,
      });
    },
    [id, create]
  );

  if (!patient) {
    return null;
  }

  return (
    <WithGroup groups={[GroupSecurity.TaskCreate]}>
      <Assignment codes={taskCodes} onChange={onSubmit} />
    </WithGroup>
  );
};

export const PatientTasksScreen: React.FC<PatientDrawerScreenProps<'Tasks'>> = () => {
  const id = usePatientId();
  const navigation = useNavigation();
  const usePatientTaskProps = usePatientTasks({ subject: id });

  const navigate = useCallback<NavigateToTask>(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TODO: Fix typing issue
    link => navigation.navigate(link.screen, link.params),
    [navigation]
  );

  const patientTaskListSWRKey = taskApi.getPatientSWRKey(id);

  return (
    <>
      <PatientHeaderSection
        id={id}
        action={PatientHeaderAssignmentButton}
        {...usePatientTaskProps}
      />
      <Section flex={1}>
        <TaskInbox
          navigate={navigate}
          taskListSWRKey={patientTaskListSWRKey}
          {...usePatientTaskProps}
        />
      </Section>
    </>
  );
};
